/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useState } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { MDXProvider } from '@mdx-js/react';
import { useLocation } from '@reach/router';
import { bind } from 'bind-event-listener';

import {
	DoDont,
	DoDontGrid,
	Example,
	FeatureFlagProvider,
	SideNavigation,
	TopNavigation,
	UserPreferencesProvider,
} from '@af/design-system-docs-ui';
import AppProvider from '@atlaskit/app-provider/app-provider';
import { getDocument } from '@atlaskit/browser-apis';
import Snippet from '@atlaskit/code/code';
import { cssMap } from '@atlaskit/css';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import Link from '@atlaskit/link/link';
import { Main, Root as PageLayout } from '@atlaskit/navigation-system';

import MetaTags from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/meta-tags';
import PropsLogger from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/utilities/props-logger';
import { DOCS_THEME_SETTINGS } from '../constants/theme';
import { useNavigation } from '../router/use-navigation';
import { getInitialColorMode } from '../utils/stored-color-mode';
import AppProviderRouterLink from './app-provider-router-link';
import Footer from './footer';
import GlobalStyles from './global-styles';
import { HomeSkipLinks } from './home-skip-links';
import { LandingPageSpinner } from './landing-page-spinner';
import SearchModal from './search';
import { SearchModalProvider } from './search/search-modal-context';

type LayoutProps = {
	children: any;
};

const shortcodes = {
	DoDont,
	DoDontGrid,
	Example,
	PropsLogger,
	Snippet,
	a: Link,
} as const;

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const homePageContentStyles = css({
	animation: `${fadeIn} 0.3s ease-in-out forwards`,
	opacity: 0,
});

const layoutContainerStyles = css({
	display: 'flex',
	minHeight: '100vh',
	flexDirection: 'column',
	transition: 'background-color 200ms ease',
});

const homeStyles = cssMap({
	layout: {
		width: '100%',
		minHeight: '100vh',
		position: 'relative',
	},
	content: {
		width: '100%',
	},
	contentWrapper: {
		opacity: 0,
		transition: 'opacity 300ms ease-out',
	},
	contentFadeIn: {
		opacity: 1,
	},
});

// Module-level flag - persists across client-side navigation, resets on full page load
let hasShownSpinnerThisSession = false;

/**
 * __Layout__
 *
 * A layout for design system docs.
 *
 */
const Layout = ({ children }: LayoutProps): JSX.Element => {
	const navigation = useNavigation();
	/**
	 * The color mode `AppProvider` starts with: the reader's saved preference (`light`, `dark` or
	 * `auto`), not a hardcoded `light`.
	 *
	 * `AppProvider` applies this to `<html>` in a mount effect, after hydration. By then the inline
	 * scripts in `gatsby-ssr.js` have already set `data-color-mode` from the same saved preference,
	 * so the reader is looking at the right colors. A hardcoded `light` would overwrite a dark
	 * reader's page with light, and the later correction would flip it back: a visible flash.
	 * Starting from the same preference makes that first write a no-op.
	 *
	 * It is read from storage, not from `data-color-mode`, because the attribute only holds the
	 * resolved `light` or `dark`. `AppProvider` also needs to know when the answer is `auto`, so it
	 * keeps following the operating system for readers who have not chosen a mode.
	 *
	 * On the server there is no storage, so it is always `auto` there, and a dark reader's client
	 * disagrees with the prerendered HTML about what `AppProvider` was seeded with. That is a
	 * deliberate trade: the alternative is seeding `auto` on both sides and correcting to the stored
	 * mode in an effect, which is the flash described above. It is safe only because the value never
	 * reaches the DOM. The root `AppProvider` renders no color-mode attributes of its own, and nothing
	 * under it renders from `useColorMode()`; the server's value there is `light`, the client's is the
	 * stored mode, so a component that did would hydrate with a mismatch. docs-ui's
	 * `color-mode-hydration.test.tsx` hydrates a `dark` client against an `auto` server for this
	 * reason.
	 *
	 * Read once, on mount: `AppProvider` only honours `defaultColorMode` then.
	 */
	const [initialColorMode] = useState(getInitialColorMode);

	const { pathname } = useLocation();
	const currentPath = pathname === '/' || pathname === '' ? '/' : pathname;

	const isHome = currentPath === '/';
	const isNews = pathname.startsWith('/news');
	const showSideNavigation = !(isHome || isNews);

	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [isSideNavExpanded, setIsSideNavExpanded] = useState(false);
	const [isHomepageLoading, setIsHomepageLoading] = useState(
		() => isHome && !hasShownSpinnerThisSession,
	);
	const [isFadingOut, setIsFadingOut] = useState(false);

	const handleSearchClose = useCallback(() => {
		setIsSearchOpen(false);
	}, []);
	const handleSearchOpen = useCallback(() => {
		setIsSearchOpen(true);
	}, []);

	useEffect(() => {
		// Keyboard shortcut to open search
		return bind(window, {
			type: 'keydown',
			listener: (event: KeyboardEvent) => {
				if (event.metaKey && event.key === 'k' && !event.repeat) {
					setIsSearchOpen(true);
				}
			},
		});
	}, []);

	const handleSideNavExpand = useCallback(
		(_args?: { screen: 'mobile' | 'desktop'; trigger?: unknown }) => {
			setIsSideNavExpanded(true);
		},
		[],
	);

	const handleSideNavCollapse = useCallback(
		(_args?: { screen: 'mobile' | 'desktop'; trigger?: unknown }) => {
			setIsSideNavExpanded(false);
		},
		[],
	);

	// Handle homepage loading state using window.load event
	useEffect(() => {
		if (!isHome) {
			// If initial page load was not homepage, mark session as done
			// so navigating to homepage later won't show the spinner
			hasShownSpinnerThisSession = true;
			setIsHomepageLoading(false);
			setIsFadingOut(false);
			return;
		}

		// Skip spinner on client-side navigation (already shown this session)
		if (hasShownSpinnerThisSession) {
			setIsHomepageLoading(false);
			setIsFadingOut(false);
			return;
		}

		// Show spinner for first full page load
		setIsHomepageLoading(true);
		setIsFadingOut(false);

		const completeLoading = () => {
			if (hasShownSpinnerThisSession) {
				return;
			}
			hasShownSpinnerThisSession = true;
			setIsFadingOut(true);
			// Fade out spinner and background simultaneously, then hide and notify
			setTimeout(() => {
				setIsHomepageLoading(false);
				// Dispatch custom event for components that need to know loading is complete
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('homepage-loading-complete'));
				}
			}, 300);
		};

		const doc = getDocument();

		// If page is already loaded, complete immediately
		if (doc?.readyState === 'complete') {
			completeLoading();
			return;
		}

		// Wait for window load event (all resources loaded)
		// Only run in browser
		if (typeof window === 'undefined') {
			completeLoading();
			return;
		}

		const unbindLoad = bind(window, { type: 'load', listener: completeLoading });

		// Fallback timeout in case load event doesn't fire (3 seconds max)
		const fallbackTimeout = setTimeout(completeLoading, 3000);

		return () => {
			unbindLoad();
			clearTimeout(fallbackTimeout);
		};
	}, [isHome, pathname]);

	return (
		<MDXProvider components={shortcodes}>
			<AppProvider
				defaultColorMode={initialColorMode}
				defaultTheme={DOCS_THEME_SETTINGS}
				routerLinkComponent={AppProviderRouterLink}
			>
				<UserPreferencesProvider>
					<FeatureFlagProvider>
						<FlagsProvider>
							<GlobalStyles />
							<MetaTags isGlobal />
							<SearchModalProvider onOpen={handleSearchOpen} onClose={handleSearchClose}>
								<div css={layoutContainerStyles}>
									{showSideNavigation ? (
										// Standard PageLayout for other pages
										<PageLayout>
											<TopNavigation
												id="top-navigation"
												onSearchOpen={handleSearchOpen}
												pathname={pathname}
												shouldUsePageLayout={true}
												isSideNavExpanded={isSideNavExpanded}
											/>
											{showSideNavigation && (
												<SideNavigation
													id="side-navigation"
													navigation={navigation}
													currentPath={currentPath}
													onExpand={handleSideNavExpand}
													onCollapse={handleSideNavCollapse}
												/>
											)}
											<Main id="main-content">
												<div key={pathname}>{children}</div>
												<Footer isHome={false} />
											</Main>
										</PageLayout>
									) : (
										// Custom simple layout for homepage
										<div css={homeStyles.layout}>
											<HomeSkipLinks
												links={[
													{ id: 'main-content', label: 'Skip to main content' },
													{ id: 'footer', label: 'Skip to footer' },
												]}
											/>
											<TopNavigation
												id="top-navigation"
												onSearchOpen={handleSearchOpen}
												pathname={pathname}
												isBottomBorderVisible={false}
												shouldUsePageLayout={false}
												isSideNavExpanded={isSideNavExpanded}
											/>
											{isHomepageLoading && <LandingPageSpinner isFadingOut={isFadingOut} />}
											<div
												css={[
													homeStyles.content,
													homeStyles.contentWrapper,
													!isHomepageLoading && homeStyles.contentFadeIn,
												]}
											>
												<div
													key={pathname}
													css={homePageContentStyles}
													role="main"
													id="main-content"
												>
													{children}
												</div>
												{!isHomepageLoading && (
													<div id="footer">
														<Footer isHome={true} />
													</div>
												)}
											</div>
										</div>
									)}
								</div>
								<SearchModal isOpen={isSearchOpen} onClose={handleSearchClose} />
							</SearchModalProvider>
						</FlagsProvider>
					</FeatureFlagProvider>
				</UserPreferencesProvider>
			</AppProvider>
		</MDXProvider>
	);
};

export default Layout;
